import { useMemo } from "react";
import { t } from "ttag";

import { CollectionRowMenu } from "metabase/common/collections/components/CollectionRowMenu";
import type { TreeItem } from "metabase/data-studio/common/types";
import {
  isCollectionData,
  isEmptyStateData,
} from "metabase/data-studio/common/utils";
import { useSelector } from "metabase/redux";
import {
  ActionIcon,
  EntityNameCell,
  FixedSizeIcon,
  Tooltip,
  type TreeTableColumnDef,
} from "metabase/ui";
import { getIsRemoteSyncReadOnly } from "metabase-enterprise/remote_sync/selectors";
import type { CollectionItem } from "metabase-types/api";

type ColumnDefProps = {
  handleUnarchiveClick: (item: Pick<CollectionItem, "id" | "name">) => void;
};

export const useColumnDef = ({ handleUnarchiveClick }: ColumnDefProps) => {
  const remoteSyncReadOnly = useSelector(getIsRemoteSyncReadOnly);

  return useMemo<TreeTableColumnDef<TreeItem>[]>(
    () => [
      {
        id: "name",
        header: t`Name`,
        enableSorting: true,
        accessorKey: "name",
        minWidth: 200,
        cell: ({ row }) => (
          <EntityNameCell
            data-testid={`${row.original.model}-name`}
            icon={row.original.icon}
            name={row.original.name}
          />
        ),
      },
      {
        id: "actions",
        width: 48,
        cell: ({ row }) => {
          const { data } = row.original;

          if (remoteSyncReadOnly || isEmptyStateData(data)) {
            return null;
          }

          if (isCollectionData(data)) {
            return <CollectionRowMenu collection={data} />;
          }

          return (
            <Tooltip label={t`Unarchive snippet`}>
              <ActionIcon
                aria-label={t`Unarchive snippet`}
                size="md"
                onClick={async (event) => {
                  event.stopPropagation();
                  event.preventDefault();
                  handleUnarchiveClick({
                    id: Number(data.id),
                    name: data.name,
                  });
                }}
              >
                <FixedSizeIcon name={"unarchive"} c="text-primary" />
              </ActionIcon>
            </Tooltip>
          );
        },
      },
    ],
    [handleUnarchiveClick, remoteSyncReadOnly],
  );
};
